import { Component } from '@theme/component';
import { debounce, getStickyOffset } from '@theme/utilities';
import { DialogCloseEvent } from '@theme/events';

class CollapsibleText extends Component {
  requiredRefs = ['content', 'contentInner', 'toggle'];

  #expanded = false;
  #resizeObserver = null;
  #handleDialogClose = null;
  #dialogComponent = null;

  connectedCallback() {
    super.connectedCallback();

    this.init();
    this.refs.toggle.addEventListener('click', this.toggle);
    
    if (window.ResizeObserver) {
      this.#resizeObserver = new ResizeObserver(debounce(() => {
        this.init();
      }, 250));
      this.#resizeObserver.observe(this.refs.contentInner);
    }

    this.#dialogComponent = this.closest('.dialog-component');
    if(this.#dialogComponent) {
      this.#handleDialogClose = () => {
        if (this.#expanded) {
          this.#close();
        }
      };
      this.#dialogComponent.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.refs.toggle) {
      this.refs.toggle.removeEventListener('click', this.toggle);
    }
    this.#resizeObserver?.disconnect();
    
    if(this.#dialogComponent && this.#handleDialogClose) {
      this.#dialogComponent.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    }
  }

  /**
   * Toggles expanded/collapsed state
   */
  toggle = () => {
    this.#expanded ? this.#close() : this.#open();
  };

  /**
   * Initializes component by checking if content exceeds height limit
   * Shows toggle button only if content is taller than limit
   */
  init() {
    // Never re-clamp an open panel: a ResizeObserver tick (image/font load) must not
    // strip `collapse-expanded` and flash the fade mask over fully expanded content.
    if (this.#expanded) return;

    const contentEl = this.refs.content;
    if (!(contentEl instanceof HTMLElement)) return;
    const heightLimitAttr = contentEl.dataset?.heightLimit;
    let heightLimit = 0;
    if (heightLimitAttr) {
      heightLimit = Number.parseFloat(heightLimitAttr);
    } else {
      heightLimit = contentEl.getBoundingClientRect().height;
    }

    if (Number.isNaN(heightLimit) || heightLimit <= 0) return;

    const { height } = this.refs.contentInner.getBoundingClientRect();

    if (height > heightLimit) {
      contentEl.classList.remove('collapse-expanded');
     this.refs.toggleButton?.classList.remove('hidden');
    } else {
      contentEl.classList.add('collapse-expanded');
      this.refs.toggleButton?.classList.add('hidden');
    }
   
  }

  /**
   * Opens content by animating to full height
   * Uses requestAnimationFrame to ensure smooth transition
   */
  #open() {
    const contentEl = this.refs.content;
    const contentInnerEl = this.refs.contentInner;
    if (!(contentEl instanceof HTMLElement) || !(contentInnerEl instanceof HTMLElement)) return;

    this.#expanded = true;
    window.requestAnimationFrame(() => {
      const innerRect = contentInnerEl.getBoundingClientRect();
      contentEl.style.setProperty('--collapse-height', `${innerRect.height}px`);
      contentEl.classList.add('collapse-expanded');
      this.#updateButtonText();
    });
  }

  /**
   * Closes content by animating to collapsed height
   * Scrolls element into view if needed when closing
   * Supports both lines-limit and height-limit modes
   */
  #close() {
    const contentEl = this.refs.content;
    if (!(contentEl instanceof HTMLElement)) return;

    const scrollIntoViewIfNeeded = () => {
      // Wait for collapse animation to complete, then check if element needs scrolling
      requestAnimationFrame(() => {
        const rect = contentEl.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const currentStickyOffset = getStickyOffset();

        // Check if element is not fully visible (above or below viewport)
        const isAboveViewport = rect.bottom < 0;
        const isBelowViewport = rect.top > viewportHeight;
        const isPartiallyVisible = rect.top < (currentStickyOffset + 50) || rect.bottom > viewportHeight;

        // If element is not visible or only partially visible, scroll it into view
        if (isAboveViewport || isBelowViewport || isPartiallyVisible) {
          const elementAbsoluteTop = rect.top + window.scrollY;
          const scrollingUp = elementAbsoluteTop < window.scrollY;
          const stickyOffset = getStickyOffset({ scrollingUp });
          const scrollPosition = elementAbsoluteTop - stickyOffset - 50;
          window.scrollTo({ top: Math.max(0, scrollPosition), behavior: 'smooth' });
          contentEl.style.transition = 'none';
          window.setTimeout(() => contentEl.style.removeProperty('transition'), 1000);
        }
      });
    };

    const linesLimit = contentEl.dataset.linesLimit;
    if (linesLimit) {
      const height = 'calc(var(--font-body-line-height) * var(--font-body-size) * var(--lines-limit))';
      contentEl.style.setProperty('--collapse-height', height);
    } 
    else {
      const heightLimit = contentEl.dataset.heightLimit;
      if (heightLimit) {
        contentEl.style.setProperty('--collapse-height', `${heightLimit}`);
      }
    }

    this.#expanded = false;
    contentEl.classList.remove('collapse-expanded');
    this.#updateButtonText();
    scrollIntoViewIfNeeded();
  }

  /**
   * Updates button text based on expanded state
   * Uses data attributes or defaults to "Show more"/"Show less"
   */
  #updateButtonText() {
    const buttonEl = this.refs.toggle;
    if (!(buttonEl instanceof HTMLElement)) return;

    const expandLabel = buttonEl.dataset.expandLabel;
    const collapseLabel = buttonEl.dataset.collapseLabel;

    buttonEl.textContent = this.#expanded ? collapseLabel : expandLabel;
  }
}

if (!customElements.get('collapsible-text')) {
  customElements.define('collapsible-text', CollapsibleText);
}

